import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { APP_NAME } from '@/lib/brand';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import FieldTooltip from '@/components/ui/FieldTooltip';
import { Check, Package, Plus, Save, Sparkles, Trash2 } from 'lucide-react';
import {
  INDUSTRY_OPTIONS, INDUSTRY_SUBTYPES, LICENSE_TYPE_OPTIONS, getPriceMatrix,
  applyIndustryToPlanForm, isCatalogIndustry, isCatalogPlan, CATALOG_MODULE_PATCH,
} from './constants';
import {
  PLAN_MODULE_TREE, CAPABILITY_OPTIONS, PLAN_CAP_FIELDS,
  DEFAULT_CAPABILITIES, DEFAULT_VOICE_POLICY,
  normalizePlanModules, previewMenu, capLabel, blankPlanForm,
} from './planCatalog';

const VERTICALES = [
  { k: 'agency', l: 'Agencias' },
  { k: 'independent', l: 'Independientes' },
  { k: 'developer', l: 'Desarrolladores' },
  { k: 'franchise', l: 'Franquicias' },
  { k: 'owner', l: 'Owner' },
  { k: 'car_dealer', l: 'Car-Dealer' },
  { k: 'education', l: 'Educacion' },
  { k: 'generic', l: 'General' },
  { k: 'retail', l: 'Retail' },
];

export default function PlanEditorDialog({ open, onOpenChange, plan, sortOrder = 1, onSaved }) {
  const [form, setForm] = useState(() => blankPlanForm(sortOrder));
  const [saving, setSaving] = useState(false);
  const [previewRole, setPreviewRole] = useState('vendor');
  const [previewType, setPreviewType] = useState('agency');

  useEffect(() => {
    if (!open) return;
    if (plan) {
      const matrix = getPriceMatrix(plan);
      setForm({
        ...blankPlanForm(),
        ...plan,
        license_type: plan.license_type || plan.target_audience || 'agency',
        industry: plan.industry || '',
        industry_subtype: plan.industry_subtype || '',
        is_sas_only: !!plan.is_sas_only,
        period_discounts: { quarterly_pct: 5, semiannual_pct: 10, annual_pct: 15, ...(plan.period_discounts || {}) },
        price: Number(plan.price ?? matrix.monthly ?? 0),
        price_matrix: matrix,
        superadmin_volume_pricing: {
          enabled: !!plan?.superadmin_volume_pricing?.enabled,
          min_licenses: Number(plan?.superadmin_volume_pricing?.min_licenses ?? 5),
          discount_pct: Number(plan?.superadmin_volume_pricing?.discount_pct ?? 0),
        },
        included_modules: normalizePlanModules(plan.included_modules),
        visible_for_types: isCatalogPlan(plan)
          ? [...(plan.visible_for_types || [])]
          : [...new Set([...(plan.visible_for_types || []), 'franchise', 'owner', 'car_dealer'])],
        monthly_caps: {
          mass_campaign_leads: -1, mass_campaign_sends: -1, contacts_monthly: -1,
          ai_outbound_calls: -1, ai_call_minutes: -1, mass_campaign_ai_calls: -1, emails_monthly: -1,
          ...(plan.monthly_caps || {}),
        },
        comm_logics_policy: { max_links: -1, lock_modification: false, ...(plan.comm_logics_policy || {}) },
        limits: {
          users: 1, contacts: 100, flows: 1, campaigns: 1, developments: -1,
          comm_recipe_call_steps: -1, storage_gb: -1, ...(plan.limits || {}),
        },
        locked_comm_logic_id: plan.locked_comm_logic_id || '',
        capabilities: { ...DEFAULT_CAPABILITIES, ...(plan.capabilities || {}) },
        voice_policy: { ...DEFAULT_VOICE_POLICY, ...(plan.voice_policy || {}) },
        features: plan.features || [],
      });
      setPreviewType(plan.license_type || plan.target_audience || 'agency');
    } else {
      setForm(blankPlanForm(sortOrder));
      setPreviewType('agency');
    }
  }, [open, plan, sortOrder]);

  const set = (patch) => setForm((prev) => ({ ...prev, ...patch }));
  const toggleModule = (key) => {
    const checked = !form.included_modules?.[key];
    const updated = { ...(form.included_modules || {}), [key]: checked };
    if (key === 'products' && checked) updated.developments = false;
    if (key === 'developments' && checked) updated.products = false;
    set({ included_modules: updated });
  };
  const setBag = (bag, key, raw) => {
    if (raw === '' || raw === '-') {
      set({ [bag]: { ...(form[bag] || {}), [key]: -1 } });
      return;
    }
    const n = parseInt(raw, 10);
    const val = Number.isFinite(n) ? n : -1;
    set({ [bag]: { ...(form[bag] || {}), [key]: val } });
  };

  const preview = useMemo(
    () => previewMenu(form.included_modules, { role: previewRole, businessType: previewType }),
    [form.included_modules, previewRole, previewType],
  );
  const vendeVoz = form.capabilities?.voice !== false;

  const save = async () => {
    if (!form.name) { toast.error('Nombre requerido'); return; }
    const payload = {
      ...form,
      price: Number(form?.price_matrix?.monthly ?? form.price ?? 0),
      license_type: form.license_type || form.target_audience || 'agency',
      included_modules: normalizePlanModules(form.included_modules),
    };
    if (['agency', 'independent'].includes(payload.target_audience)) {
      payload.trial_days = Math.min(15, Number(payload.trial_days || 15));
    } else {
      payload.trial_days = 0;
    }
    setSaving(true);
    try {
      if (plan?.id) await api.put(`/admin/dynamic-plans/${plan.id}`, payload);
      else await api.post('/admin/dynamic-plans', payload);
      toast.success(plan?.id ? 'Plan actualizado' : 'Plan creado');
      onOpenChange(false);
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const magicDescription = async () => {
    try {
      const res = await api.post('/admin/dynamic-plans/magic-description', {
        license_type: form.license_type, limits: form.limits, ai_tokens: form.ai_tokens,
      });
      set({ description: res.data?.description || form.description });
      toast.success('Descripción generada');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo generar');
    }
  };
  const magicFeatures = async () => {
    try {
      const res = await api.post('/admin/dynamic-plans/magic-features', {
        license_type: form.license_type, industry: form.industry, industry_subtype: form.industry_subtype,
        limits: form.limits, monthly_caps: form.monthly_caps, included_modules: form.included_modules,
        ai_tokens: form.ai_tokens, is_sas_only: form.is_sas_only,
      });
      const generated = (res.data?.features || []).filter(Boolean);
      if (!generated.length) { toast.warning('Magic AI no generó beneficios'); return; }
      set({ features: generated });
      toast.success(`${generated.length} beneficios`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo generar');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl max-h-[92vh] overflow-y-auto bg-[#0a0c12] border-white/[0.06]" data-testid="plan-editor-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-amber-400 flex items-center gap-2">
            <Package className="w-5 h-5" />{plan ? 'Editar plan' : 'Nuevo plan'}
          </DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
          <div className="col-span-2">
            <div className="flex items-center gap-1.5"><Label className="text-[10px]">Nombre *</Label><FieldTooltip tip="Nombre interno y, si está visible, el de la landing." /></div>
            <Input value={form.name} onChange={(e) => set({ name: e.target.value })} className="bg-black/20 border-white/10 text-xs mt-1" data-testid="pf-name" />
          </div>
          <div>
            <Label className="text-[10px]">Precio mensual</Label>
            <Input type="number" value={form.price} onChange={(e) => {
              const val = parseFloat(e.target.value) || 0;
              set({ price: val, price_matrix: { ...(form.price_matrix || {}), monthly: val } });
            }} className="bg-black/20 border-white/10 text-xs mt-1" data-testid="pf-price" />
          </div>
          <div>
            <Label className="text-[10px]">Moneda</Label>
            <Select value={form.currency} onValueChange={(v) => set({ currency: v })}>
              <SelectTrigger className="bg-black/20 border-white/10 text-xs mt-1"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="MXN">MXN</SelectItem><SelectItem value="USD">USD</SelectItem></SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[10px]">Días de prueba</Label>
            <Input type="number" value={form.trial_days} onChange={(e) => set({ trial_days: parseInt(e.target.value, 10) || 0 })} disabled={!['agency', 'independent'].includes(form.target_audience)} className="bg-black/20 border-white/10 text-xs mt-1" data-testid="pf-trial" />
          </div>
          <div>
            <Label className="text-[10px]">Orden</Label>
            <Input type="number" value={form.sort_order} onChange={(e) => set({ sort_order: parseInt(e.target.value, 10) || 1 })} className="bg-black/20 border-white/10 text-xs mt-1" data-testid="pf-sort" />
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-4 mt-2" data-testid="pf-tres-columnas">
          <section className="rounded-xl border border-white/10 bg-black/20 p-3 space-y-3" data-testid="pf-col-verticales">
            <h4 className="text-[11px] font-semibold text-white tracking-wide">1. Quién lo ve</h4>
            <p className="text-[9px] text-muted-foreground">Verticales de la landing y del alta de cuenta. Un plan “BDC autos” solo marca Car-Dealer.</p>
            <div className="grid grid-cols-2 gap-1.5" data-testid="pf-industry-picker">
              {INDUSTRY_OPTIONS.map((ind) => (
                <button key={ind.value} type="button" onClick={() => {
                  const firstSub = (INDUSTRY_SUBTYPES[ind.value] || [])[0];
                  const patch = applyIndustryToPlanForm(form, ind.value, firstSub);
                  set(patch);
                  setPreviewType(isCatalogIndustry(ind.value) ? 'agency' : (firstSub?.btype || form.license_type));
                }} className={`p-2 rounded-lg border text-left text-[10px] ${form.industry === ind.value ? 'border-amber-400/50 bg-amber-500/10 text-white' : 'border-white/[0.06] text-white/45'}`} data-testid={`pf-industry-${ind.value}`}>
                  {ind.icon} {ind.label}
                </button>
              ))}
            </div>
            {form.industry && INDUSTRY_SUBTYPES[form.industry] && (
              <div className="grid grid-cols-2 gap-1.5">
                {INDUSTRY_SUBTYPES[form.industry].map((sub) => (
                  <button key={sub.value} type="button" onClick={() => {
                    const catalogLt = isCatalogIndustry(form.industry) ? form.industry : null;
                    set({
                      industry_subtype: sub.value,
                      license_type: catalogLt || sub.btype,
                      target_audience: catalogLt || sub.btype,
                      ...(catalogLt ? { included_modules: { ...(form.included_modules || {}), ...CATALOG_MODULE_PATCH } } : {}),
                    });
                    setPreviewType(catalogLt ? 'agency' : sub.btype);
                  }} className={`p-2 rounded-lg border text-left text-[10px] ${form.industry_subtype === sub.value ? 'border-teal-400/50 bg-teal-500/10 text-white' : 'border-white/[0.06] text-white/45'}`} data-testid={`pf-subtype-${sub.value}`}>
                    <p className="font-bold">{sub.label}</p>
                    <p className="text-[8px] text-muted-foreground">{sub.desc}</p>
                  </button>
                ))}
              </div>
            )}
            <div>
              <Label className="text-[10px] text-white/60">Visible en landing para</Label>
              <div className="flex flex-wrap gap-1.5 mt-1">
                {VERTICALES.map((bt) => {
                  const checked = (form.visible_for_types || []).includes(bt.k);
                  return (
                    <label key={bt.k} className={`flex items-center gap-1.5 px-2 py-1 rounded-lg border cursor-pointer text-[10px] ${checked ? 'bg-[#A5D62C]/10 border-[#A5D62C]/30 text-[#A5D62C]' : 'border-white/10 text-white/40'}`} data-testid={`pf-vft-${bt.k}`}>
                      <input type="checkbox" className="sr-only" checked={checked} onChange={() => {
                        const arr = form.visible_for_types || [];
                        set({ visible_for_types: checked ? arr.filter((x) => x !== bt.k) : [...arr, bt.k] });
                      }} />
                      {bt.l}
                    </label>
                  );
                })}
              </div>
            </div>
            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-2"><Switch checked={form.is_active} onCheckedChange={(v) => set({ is_active: v })} /><Label className="text-[10px]">Activo (se puede asignar)</Label></div>
              <div className="flex items-center gap-2"><Switch checked={form.is_visible} onCheckedChange={(v) => set({ is_visible: v })} data-testid="pf-visible" /><Label className="text-[10px]">Visible en landing de {APP_NAME}</Label></div>
              <div className="flex items-center gap-2"><Switch checked={form.is_popular} onCheckedChange={(v) => set({ is_popular: v })} /><Label className="text-[10px]">Popular</Label></div>
              <div className="flex items-center gap-2"><Switch checked={!!form.is_sas_only} onCheckedChange={(v) => set({ is_sas_only: v })} data-testid="pf-sas-toggle" /><Label className="text-[10px]">Venta SAS (ocultar precio)</Label></div>
            </div>
            <div>
              <Label className="text-[10px]">Descripción (landing)</Label>
              <Textarea value={form.description} onChange={(e) => set({ description: e.target.value })} className="bg-black/20 border-white/10 text-xs mt-1 min-h-[72px]" data-testid="pf-desc" />
            </div>
          </section>

          <section className="rounded-xl border border-white/10 bg-black/20 p-3 space-y-3" data-testid="pf-col-arbol">
            <h4 className="text-[11px] font-semibold text-white tracking-wide">2. Módulos</h4>
            <p className="text-[9px] text-muted-foreground">Prende lo que el tenant ve. Super Admin siempre configura. Desarrollos y Productos y Servicios se excluyen. Educación / General encienden el catálogo al elegir la industria.</p>
            {PLAN_MODULE_TREE.map((grupo) => (
              <div key={grupo.group}>
                <p className="text-[10px] uppercase tracking-wider text-amber-300/80 mb-1">{grupo.group}</p>
                <div className="space-y-1">
                  {grupo.items.map((mod) => {
                    const checked = !!form.included_modules?.[mod.key];
                    const excluded =
                      (mod.key === 'products' && form.included_modules?.developments) ||
                      (mod.key === 'developments' && form.included_modules?.products);
                    return (
                      <label key={mod.key} className={`flex items-start gap-2 px-2 py-1.5 rounded-lg border cursor-pointer ${checked ? 'bg-sky-500/10 border-sky-500/20' : 'border-white/10'}`} data-testid={`pf-module-${mod.key}`}>
                        <input type="checkbox" className="sr-only" checked={checked} onChange={() => toggleModule(mod.key)} />
                        <div className={`mt-0.5 w-3.5 h-3.5 rounded border flex items-center justify-center shrink-0 ${checked ? 'bg-sky-500 border-sky-500' : 'border-white/20'}`}>
                          {checked && <Check className="w-2.5 h-2.5 text-white" />}
                        </div>
                        <div className="min-w-0">
                          <div className="flex items-center gap-1">
                            <span className={`text-[11px] ${checked ? 'text-sky-200' : 'text-white/50'}`}>{mod.label}</span>
                            <FieldTooltip tip={mod.tip} size={10} />
                            {mod.saAlways && <span className="text-[8px] text-amber-400/80">SA siempre</span>}
                            {excluded && <span className="text-[8px] text-amber-400/70">excluye el otro</span>}
                            {mod.governed && <span className="text-[8px] text-amber-400/70">lo gobierna la vertical</span>}
                          </div>
                        </div>
                      </label>
                    );
                  })}
                </div>
              </div>
            ))}
          </section>

          <section className="rounded-xl border border-white/10 bg-black/20 p-3 space-y-3" data-testid="pf-col-caps">
            <h4 className="text-[11px] font-semibold text-white tracking-wide">3. Caps y automatización</h4>
            <p className="text-[9px] text-muted-foreground">-1 = ilimitado. El cliente nunca ve dólares. Un plan sin voz no vende voz.</p>
            <div className="grid grid-cols-2 gap-2">
              {PLAN_CAP_FIELDS.map((f) => (
                <div key={f.key}>
                  <div className="flex items-center gap-1">
                    <Label className="text-[9px] text-muted-foreground">{f.label}</Label>
                    <FieldTooltip tip={f.tip} size={10} />
                  </div>
                  <Input type="number" value={form[f.bag]?.[f.key] ?? -1} onChange={(e) => setBag(f.bag, f.key, e.target.value)} className="bg-black/20 border-white/10 text-xs mt-0.5" data-testid={`pf-cap-${f.key}`} />
                </div>
              ))}
            </div>
            {[...new Set(CAPABILITY_OPTIONS.map((c) => c.group || 'Voz y llamadas'))].map((grupo) => (
              <div key={grupo}>
                <Label className="text-[10px] text-sky-300">{grupo}</Label>
                <div className="mt-1 space-y-1">
                  {CAPABILITY_OPTIONS.filter((c) => (c.group || 'Voz y llamadas') === grupo).map((cap) => (
                    <div key={cap.key} className="flex items-center gap-2">
                      <Switch checked={form.capabilities?.[cap.key] ?? cap.on} onCheckedChange={(v) => set({ capabilities: { ...(form.capabilities || {}), [cap.key]: v } })} data-testid={`pf-cap-${cap.key}`} />
                      <Label className="text-[10px]">{cap.label}</Label>
                      <FieldTooltip tip={cap.tip} size={10} />
                    </div>
                  ))}
                </div>
                {grupo === 'Voz y llamadas' && !vendeVoz && <p className="text-[9px] text-amber-300 mt-1" data-testid="pf-sin-voz">Este plan no vende voz en la landing.</p>}
              </div>
            ))}
            <div>
              <Label className="text-[10px] text-white/60">Tokens IA / mes</Label>
              <div className="grid grid-cols-2 gap-2 mt-1">
                <Input type="number" value={form.ai_tokens?.monthly_limit || 0} onChange={(e) => set({ ai_tokens: { ...form.ai_tokens, monthly_limit: parseInt(e.target.value, 10) || 0 } })} className="bg-black/20 border-white/10 text-xs" />
                <div className="flex items-center gap-2"><Switch checked={form.ai_tokens?.block_on_exceed} onCheckedChange={(v) => set({ ai_tokens: { ...form.ai_tokens, block_on_exceed: v } })} /><Label className="text-[9px]">Bloquear al exceder</Label></div>
              </div>
            </div>
            <div className="rounded-lg border border-emerald-500/20 bg-emerald-500/5 p-2" data-testid="pf-preview-menu">
              <div className="flex items-center justify-between mb-1">
                <Label className="text-[10px] text-emerald-200">Preview: así lo verá</Label>
                <div className="flex gap-1">
                  <select value={previewRole} onChange={(e) => setPreviewRole(e.target.value)} className="bg-black/40 border border-white/10 rounded text-[9px] h-6 px-1" data-testid="pf-preview-role">
                    <option value="vendor">un vendedor</option>
                    <option value="admin">un admin</option>
                  </select>
                  <select value={previewType} onChange={(e) => setPreviewType(e.target.value)} className="bg-black/40 border border-white/10 rounded text-[9px] h-6 px-1" data-testid="pf-preview-type">
                    {LICENSE_TYPE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </div>
              </div>
              <p className="text-[9px] text-muted-foreground mb-1">
                Leads/mes: {capLabel(form.monthly_caps?.contacts_monthly ?? form.limits?.contacts)}
                {vendeVoz ? '' : ' · sin voz'}
              </p>
              <div className="flex flex-wrap gap-1">
                {preview.length === 0 && <span className="text-[9px] text-white/40">Nada visible</span>}
                {preview.map((l) => (
                  <span key={l} className="text-[9px] px-1.5 py-0.5 rounded bg-emerald-500/10 text-emerald-200 border border-emerald-500/20">{l}</span>
                ))}
              </div>
            </div>
          </section>
        </div>

        <div className="flex flex-wrap gap-2 mt-3">
          <Button type="button" variant="outline" onClick={magicDescription} className="text-[11px] h-8 border-violet-400/30 text-violet-200" data-testid="pf-magic-ai-description">
            <Sparkles className="w-3 h-3 mr-1" /> Descripción
          </Button>
          <Button type="button" variant="outline" onClick={magicFeatures} className="text-[11px] h-8 border-fuchsia-400/30 text-fuchsia-200" data-testid="pf-magic-ai-features">
            <Sparkles className="w-3 h-3 mr-1" /> 3-4 beneficios
          </Button>
          <Button type="button" variant="outline" onClick={() => set({ features: [...(form.features || []), ''] })} className="text-[11px] h-8 border-white/10">
            <Plus className="w-3 h-3 mr-1" /> Beneficio
          </Button>
        </div>
        {(form.features || []).length > 0 && (
          <div className="space-y-1">
            {form.features.map((f, i) => (
              <div key={i} className="flex gap-1 items-start">
                <Textarea value={f} onChange={(e) => {
                  const next = [...form.features]; next[i] = e.target.value; set({ features: next });
                }} rows={1} className="bg-black/20 border-white/10 text-xs flex-1 min-h-[28px]" />
                <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-red-400" onClick={() => set({ features: form.features.filter((_, j) => j !== i) })}><Trash2 className="w-3 h-3" /></Button>
              </div>
            ))}
          </div>
        )}

        <Button onClick={save} disabled={saving} className="w-full bg-amber-600 hover:bg-amber-500 text-xs mt-2" data-testid="save-plan-btn">
          <Save className="w-3.5 h-3.5 mr-1" /> {saving ? 'Guardando…' : (plan ? 'Guardar cambios' : 'Crear plan')}
        </Button>
      </DialogContent>
    </Dialog>
  );
}
